/* Bulk binding edits: what ran, what it changed, and what a revert would put back.
 *
 * D-04, laid out as the redesign draws it: the starter card, the ledger of recent runs, and one card
 * reporting the selected run — its four counts, its chunks, and its revert. Every control is wired
 * to the real routes — the runs listing, the journal report or preview, and the 202 undo.
 *
 * **The screen's whole job is one distinction: a revert puts back what the apply *did*, not what it
 * intended.** A binding already in the requested state before the run is `skipped`, and the revert
 * does not touch it — reverting it would be a change nobody asked for against a state that predates
 * the operation. So the revert button says how many rows it will actually move, taken from `applied`
 * alone, and the counts sit above it.
 *
 * **The report is fetched on selection with no confirmation, because it writes nothing.** It is a
 * GET, journalled nowhere, reads no customer source, and is authorized as a read — the contract
 * states the reason: requiring `update` to see what an edit would do is how somebody runs the edit
 * to find out.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { BulkStarter } from './BulkStarter';

import { reportSource, useBulkReport, useBulkRuns, useUndoBulkRun } from '../api/bulk';
import {
  STATE_TONE,
  accountsForEveryItem,
  actionLabel,
  chunkRows,
  countsOf,
  revertStanding,
  revertableCount,
  undoReach,
} from '../model/presentation';
import type { BulkReport, BulkRun } from '@/shared/api/generated/rules';

/** What the page has just been told the API accepted, said once above the ledger. */
type Accepted = 'started' | 'reverted';

export function BulkRunsScreen(): ReactElement {
  const { t } = useTranslation();
  const runs = useBulkRuns();
  const [selected, setSelected] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<Accepted | null>(null);
  const items = runs.data ?? [];
  /* The newest run's report is open until somebody picks another, as the design draws it: the
     first question on this page is what the last change did, and an empty space under the ledger
     waiting for a click answers nothing. */
  const current = items.find((run) => run.run_id === selected) ?? items[0] ?? null;
  const report = useBulkReport(current);
  const undo = useUndoBulkRun();

  return (
    <div className="lq-bk">
      <PageHeader
        title={t('bulk.title')}
        lede={t('bulk.intro')}
        meta={
          runs.isSuccess ? (
            <span>
              {t('bulk.metaLine', {
                count: items.length,
                revertable: revertableCount(items),
              })}
            </span>
          ) : undefined
        }
      />

      {accepted === null ? null : (
        <div className="lq-tintband lq-tintband--ok" role="status">
          <div className="lq-tintband__text">
            <strong>
              {accepted === 'started'
                ? t('bulk.start.accepted.title')
                : t('bulk.undo.acceptedTitle')}
            </strong>{' '}
            {accepted === 'started'
              ? t('bulk.start.accepted.body')
              : t('bulk.undo.accepted')}
          </div>
          <button
            type="button"
            className="lq-tintband__dismiss"
            aria-label={t('bulk.dismiss')}
            onClick={() => {
              setAccepted(null);
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* First, because it is the act the rest of the page is the record of. */}
      <BulkStarter
        onAccepted={() => {
          setAccepted('started');
          setSelected(null);
        }}
      />

      {runs.isPending && <SkeletonRows rows={4} columns={7} />}

      {runs.isError && (
        <Alert tone="error" title={t('bulk.error.title')}>
          {runs.error.message}
        </Alert>
      )}

      {runs.isSuccess && (
        <section className="lq-cp__card lq-cp__card--table" aria-labelledby="bk-runs">
          <div className="lq-cp__cardHead">
            <h2 id="bk-runs" className="lq-cp__cardTitle">
              {t('bulk.caption')}
            </h2>
          </div>
          <div className="lq-cp__scroll">
            <table className="lq-table lq-table--ledger lq-cp__table lq-bk__ledger">
              <caption className="lq-visually-hidden">{t('bulk.caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('bulk.column.action')}</th>
                  <th scope="col">{t('bulk.column.items')}</th>
                  <th scope="col">{t('bulk.column.chunk')}</th>
                  <th scope="col">{t('bulk.column.state')}</th>
                  <th scope="col">{t('bulk.column.by')}</th>
                  <th scope="col">{t('bulk.column.undo')}</th>
                  <th scope="col" className="lq-table__actions">
                    <span className="lq-visually-hidden">{t('bulk.column.report')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((run) => (
                  <RunRow
                    key={run.run_id}
                    run={run}
                    selected={run.run_id === current?.run_id}
                    onSelect={() => {
                      setSelected(run.run_id);
                      undo.reset();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {items.length === 0 ? (
            <p className="lq-bk__none">
              {t('bulk.empty.title')} {t('bulk.empty.guidance')}
            </p>
          ) : null}
        </section>
      )}

      {/* `/v1/rules/bulk` is `ORDER BY requested_at DESC LIMIT 50` with no cursor — S-02 records
          that gap. Saying so is the honest half a screen can do without the backend change. */}
      {items.length >= 50 && <p className="lq-bk__note">{t('bulk.truncated')}</p>}

      {current !== null && (
        <RunReport
          run={current}
          report={report}
          undo={undo}
          onReverted={() => {
            setAccepted('reverted');
          }}
        />
      )}
    </div>
  );
}

/* The revert deadline as a reader says it — "6 Nov, 07:05", day first and on a 24-hour clock, as
 * the design writes it — with the exact instant on hover. An ISO string made the one figure a reader
 * checks before reverting the hardest to read. */
const DEADLINE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function deadline(iso: string): string {
  const when = new Date(iso);
  return Number.isNaN(when.getTime()) ? iso : DEADLINE.format(when);
}

function RunRow({
  run,
  selected,
  onSelect,
}: {
  readonly run: BulkRun;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const standing = revertStanding(run);

  return (
    <tr onClick={onSelect} className="lq-bk__row">
      <td data-label={t('bulk.column.action')}>
        {t(`bulk.action.${actionLabel(run)}`)}{' '}
        {/* A run has no name — the id is its only identity — but the full 36-character UUID is the
            raw-id-as-label defect: shortened, full value on hover. */}
        <span className="lq-bk__runId" title={run.run_id}>
          {t('bulk.run.shortId', { id: run.run_id.slice(0, 8) })}
        </span>
      </td>
      <td data-label={t('bulk.column.items')}>{run.item_count.toLocaleString()}</td>
      <td data-label={t('bulk.column.chunk')}>{run.chunk_size.toLocaleString()}</td>
      <td data-label={t('bulk.column.state')}>
        <span
          className={`lq-tag lq-tag--bk-${STATE_TONE[run.state]}`}
          title={t(`bulk.stateHint.${run.state}`)}
        >
          {t(`bulk.state.${run.state}`)}
        </span>
      </td>
      <td
        data-label={t('bulk.column.by')}
        className="lq-bk__who"
        title={run.requested_by}
      >
        {personName(run.requested_by)}
      </td>
      <td data-label={t('bulk.column.undo')}>
        {/* Five standings, not a boolean — `undoable: false` covers four different facts, and a
            run that *was* revertable an hour ago and is not now looks like a bug unless the cell
            says which fact applies. */}
        <span
          className={standing === 'available' ? 'lq-bk__undoOk' : 'lq-bk__undoNo'}
          title={run.undo_expires_at ?? undefined}
        >
          {standing === 'available' &&
          run.undo_expires_at !== null &&
          run.undo_expires_at !== undefined
            ? t('bulk.undo.availableUntil', { when: deadline(run.undo_expires_at) })
            : t(`bulk.standing.${standing}`)}
        </span>
      </td>
      <td className="lq-table__actions">
        <button
          type="button"
          className="lq-cp__headButton"
          aria-pressed={selected}
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
        >
          {selected ? t('bulk.select.selected') : t('bulk.select.show')}
        </button>
      </td>
    </tr>
  );
}

function RunReport({
  run,
  report,
  undo,
  onReverted,
}: {
  readonly run: BulkRun;
  readonly report: ReturnType<typeof useBulkReport>;
  readonly undo: ReturnType<typeof useUndoBulkRun>;
  readonly onReverted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  /* The journal for a run that has one, the dry-run for one that does not — and the heading says
     which, because "what the run changed" over a preview is the exact conflation this split exists
     to end. */
  const preview = reportSource(run.state) !== 'report';
  const action = t(`bulk.action.${actionLabel(run)}`);

  return (
    <section className="lq-cp__card" aria-labelledby="bk-report">
      <h2 id="bk-report" className="lq-cp__cardTitle">
        {preview
          ? t('bulk.report.previewTitle', { action })
          : t('bulk.report.title', { action })}
      </h2>

      {report.isPending && <SkeletonRows rows={2} />}
      {report.isError && <Alert tone="error">{report.error.message}</Alert>}

      {report.isSuccess && report.data !== null && (
        <>
          {/* Every status including the zeroes: "0 changed of 400" and "400 changed" must not look
              alike, which a report showing only what happened would let them. */}
          <div className="lq-bk__counts">
            {countsOf(report.data).map((entry) => (
              <div
                key={entry.status}
                className="lq-bk__stat"
                title={t(`bulk.statusNote.${entry.status}`)}
              >
                <div className="lq-bk__statKey">{t(`bulk.status.${entry.status}`)}</div>
                <div className={`lq-bk__count lq-bk__count--${entry.status}`}>
                  {entry.count.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {!accountsForEveryItem(report.data) && (
            <Alert tone="warning" title={t('bulk.unaccounted.title')}>
              {t('bulk.unaccounted.body', {
                counted: countsOf(report.data).reduce((sum, e) => sum + e.count, 0),
                total: report.data.total_items,
              })}
            </Alert>
          )}

          {/* A chunk is committed whole or not at all, so each bar is full, empty, or the
              indeterminate fill of the one being written — and carries no number. */}
          <ol className="lq-bk__chunks" aria-label={t('bulk.chunks.title')}>
            {chunkRows(run, report.data).map((chunk, index) => (
              <li
                key={chunk.label}
                className="lq-bk__chunk"
                title={chunk.error ?? chunk.label}
              >
                <div className={`lq-bk__chunkBar lq-bk__chunkBar--${chunk.verdict}`} />
                <div className="lq-bk__chunkLabel">
                  {t('bulk.chunk.label', {
                    n: index + 1,
                    verdict: t(`bulk.chunk.${chunk.verdict}`),
                  })}
                </div>
              </li>
            ))}
          </ol>

          <RevertSection
            run={run}
            data={report.data}
            undo={undo}
            onReverted={onReverted}
          />
        </>
      )}
    </section>
  );
}

function RevertSection({
  run,
  data,
  undo,
  onReverted,
}: {
  readonly run: BulkRun;
  readonly data: BulkReport;
  readonly undo: ReturnType<typeof useUndoBulkRun>;
  readonly onReverted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const standing = revertStanding(run);
  const reach = undoReach(data);

  return (
    <div className="lq-bk__revert">
      <h3 className="lq-bk__revertTitle">{t('bulk.revert.title')}</h3>
      {standing === 'available' ? (
        <>
          {/* The number comes from `applied`, never from `total_items`. This is the whole screen:
              promising 412 when 374 will move is the misleading version. */}
          <p className="lq-bk__revertText">
            {t('bulk.undo.explain', { reach, total: data.total_items })}{' '}
            {run.undo_expires_at !== null && run.undo_expires_at !== undefined
              ? t('bulk.undo.window', { when: deadline(run.undo_expires_at) })
              : t('bulk.undo.windowOpen')}
          </p>
          <button
            type="button"
            className="lq-cp__headButton lq-bk__revertButton"
            disabled={undo.isPending}
            onClick={() => {
              undo.mutate(run.run_id, { onSuccess: onReverted });
            }}
          >
            {t('bulk.undo.action', { reach })}
          </button>
          {undo.isError && (
            <Alert tone="error" title={t('bulk.undo.failed')}>
              {undo.error.message}
            </Alert>
          )}
        </>
      ) : (
        <p className="lq-bk__revertText">{t(`bulk.blocked.${standing}`)}</p>
      )}
    </div>
  );
}
